import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import AppModal from "../../common/modal/AppModal";
import i18n from "i18next";
import IdealStudio from "../../../../core/editor/main";
import { isSlide, isBox, isContainedView } from "../../../../common/utils";
import { ID_PREFIX_BOX } from "../../../../common/constants";
import { randomPositionGenerator } from "../../clipboard/clipboard.utils";
import { createBox } from "../../../../common/common_tools";
import getMediaBoxSize from "../../../utils/getMediaBoxSize";
import { spinForSceneAdd } from "../../../utils/sceneAddSpinner";
import { fetchOrgFiles } from "../../../../common/server_utils.es6";
import { isClmAllowedPlugin } from "../../../../common/clm";
import { addSound } from "../../../../common/actions";
import AssetGrid from "../elements_library/ui/AssetGrid";
import AssetCell from "../elements_library/ui/AssetCell";
import AssetPlaceholder from "../elements_library/ui/AssetPlaceholder";
import LazyAssetImage from "../elements_library/ui/LazyAssetImage";
import PanelSearch from "../elements_library/ui/PanelSearch";
import EmptyState from "../elements_library/ui/EmptyState";
import {
  LuSearch,
  LuX,
  LuImage,
  LuMusic2,
  LuClapperboard,
  LuFileText,
  LuFileSpreadsheet,
  LuUserRound,
  LuMicVocal,
  LuMonitorPlay,
  LuWandSparkles,
  LuDownload,
  LuMousePointerClick,
  LuPlay,
  LuPause,
  LuLoaderCircle,
} from "react-icons/lu";
import { downloadFile } from "./downloadFile";

const TYPE_PLUGIN_CANDIDATES = {
  image: { media: "image", names: ["HotspotImages"] },
  animation: { media: "image", names: ["HotspotImages"] },
  video: { media: "video", names: ["EnrichedPlayer"] },
  audio: { media: "audio", names: ["AudioCue", "EnrichedAudio"] },
  pdf: { media: "pdf", names: ["EnrichedPDF"] },
  html: { media: "html", names: ["HtmlContent"] },
};
// CLM courses: only allowlisted plugins may be added (spec §3.3 item 1) — see
// MyFilesPanel.jsx's twin of this function for the full rationale.
const resolvePlugin = (fileType, isClm) => {
  const def = TYPE_PLUGIN_CANDIDATES[(fileType || "").toLowerCase()];
  if (!def) return null;
  const name = def.names.find(
    (n) => IdealStudio.Plugins.get(n) && (!isClm || isClmAllowedPlugin(n))
  );
  return name ? { name, media: def.media } : null;
};

const FILE_TYPE_MAP = {
  // "Resim" = statik görseller. DB fileType'ı kaba kategori ('image') tuttuğu
  // için gif/webp de 'image'dir; statik/hareketli ayrımı FILE_EXTENSION_MAP ile
  // uzantıdan yapılır (aşağı bak).
  image: ["image"],
  audio: ["audio", "audio/mpeg", "audio/wav", "audio/ogg", "audio/mp3", "clone-voice"],
  video: [
    "video",
    "video/mp4",
    "video/webm",
    "video/mov",
    "course-video",
    "tavus-video",
    "lipsync-video",
    "azure-avatar",
    "video/quicktime",
    // "animation" (animate-v2 webp/gif) bilerek yok — "Hareketli Resim" filtresinde.
  ],
  // pdf ve csv fileExtension ile gönderilir, bu map'te yoklar

  avatar: ["tavus-video", "lipsync-video", "azure-avatar"],
  clonevoice: ["clone-voice"],
  coursevideo: ["course-video"],
  transcription: ["transcription-document"],
  // "Hareketli Resim": animate-v2 çıktıları (fileType='animation', daima
  // webp/gif) + fileType='image' olup uzantısı gif/webp olan yüklenen/AI
  // görseller. Uzantı whitelist'i (FILE_EXTENSION_MAP) ile ayrılır.
  animatedimage: ["animation", "image"],
};

// image / animatedimage filtresi fileType'a EK olarak uzantı whitelist'i de
// gönderir (backend fileType[] AND fileExtension[] birlikte uygular). "Resim"
// statik uzantılarla sınırlanır, "Hareketli Resim" yalnız gif/webp'i yakalar.
// Uzantılar DB'de noktasız saklanır (upload/animate-v2/image-editing hepsi
// `.replace('.','')`).
const FILE_EXTENSION_MAP = {
  image: ["png", "jpg", "jpeg", "svg", "bmp"],
  animatedimage: ["gif", "webp", "apng", "avif"],
};

const IMAGE_LIKE_EXT = ["png", "jpg", "jpeg", "webp", "gif", "bmp", "svg"];
const isImageLikeUrl = (url) => {
  const clean = (url || "").split("?")[0];
  const dot = clean.lastIndexOf(".");
  return dot !== -1 && IMAGE_LIKE_EXT.includes(clean.substring(dot + 1).toLowerCase());
};

const formatDuration = (sec) => {
  const s = Math.round(sec || 0);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

// Küçük yuvarlak-kare aksiyon butonu (ses satırı ekleme butonları).
const miniBtnStyle = {
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  background: "#fff",
  color: "#6b7280",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

const OrgFilesPanel = (props) => {
  const {
    isClm,
    onNavItemsAdded,
    onIndexSelected,
    onNavItemSelected,
    navItemsIds,
    navItems,
    navItemSelected,
    containedViews,
    containedViewSelected,
    boxes,
    onBoxAdded,
    onElementSelected,
    boxSelected,
    pluginToolbars,
    fileModalResult,
    onClose,
  } = props;

  const [files, setFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileTypeFilter, setFileTypeFilter] = useState("all");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Grup görünümü state
  const [viewMode, setViewMode] = useState("list"); // "list" | "grouped"
  const [groups, setGroups] = useState([]); // groupBy=true sonucu
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState(null);
  const [groupsPage, setGroupsPage] = useState(1);
  const [groupsTotalPages, setGroupsTotalPages] = useState(1);
  const [selectedGroup, setSelectedGroup] = useState(null); // null = grup listesi, {trainingId, trainingName} = içeride

  const filesCache = useRef({});
  const groupsCache = useRef({});

  const [playingId, setPlayingId] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [durations, setDurations] = useState({}); // cardId -> saniye (metadata/play ile dolar)
  // Metadata yüklenemeyen (bozuk/CORS/Infinity) satırlar — spinner sonsuz dönmesin,
  // "0:00" fallback'ine düşer.
  const [durationFailed, setDurationFailed] = useState({});
  const audioRef = useRef(null);
  // Canlı Audio elemanının ait olduğu kart id'si — pause sonrası aynı satırı
  // kaldığı yerden sürdürmek/seek etmek için tutulur.
  const activeAudioIdRef = useRef(null);
  const dispatch = useDispatch();

  // In Replace-File mode, open the filter that matches the replaced item's type
  // (same as My Files) instead of staying on "All".
  useEffect(() => {
    if (fileModalResult && fileModalResult.accept) {
      const acc = fileModalResult.accept.toLowerCase();
      if (acc.includes("pdf")) setFileTypeFilter("pdf");
      else if (acc.includes("image")) setFileTypeFilter("image");
      else if (acc.includes("video")) setFileTypeFilter("video");
      else if (acc.includes("audio")) setFileTypeFilter("audio");
    }
  }, [fileModalResult]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // id.url'i taze bir Audio'ya yükleyip atTime'dan çalar. Süre metadata gelince
  // durations'a yazılır (satırdaki 0:00 / X:XX için).
  const startAudio = (id, url, atTime = 0) => {
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(url);
    if (atTime) audio.currentTime = atTime;
    audio.onloadedmetadata = () =>
      setDurations((d) => (d[id] ? d : { ...d, [id]: audio.duration }));
    audio.onended = () => {
      setPlayingId(null);
      setCurrentTime(0);
    };
    audio.ontimeupdate = () => setCurrentTime(audio.currentTime);
    audioRef.current = audio;
    activeAudioIdRef.current = id;
    audio.play().catch(() => {});
    setPlayingId(id);
    setCurrentTime(atTime);
  };

  const togglePlayAudio = (id, url) => {
    if (playingId === id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (activeAudioIdRef.current === id && audioRef.current) {
      audioRef.current.play().catch(() => {});
      setPlayingId(id);
      return;
    }
    startAudio(id, url);
  };

  const seekAudio = (id, url, dur, e) => {
    if (!dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const time = ratio * dur;
    if (activeAudioIdRef.current === id && audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
      if (playingId !== id) {
        audioRef.current.play().catch(() => {});
        setPlayingId(id);
      }
      return;
    }
    startAudio(id, url, time);
  };

  // Ses dosyasının süresini almak için yardımcı fonksiyon
  const getAudioDuration = (url) => {
    return new Promise((resolve) => {
      const audio = new Audio();
      audio.addEventListener("loadedmetadata", () => resolve(audio.duration));
      audio.addEventListener("error", () => resolve(30));
      audio.src = url;
    });
  };

  // Ses dosyasını sahnenin soundList'ine ekler (narration'ın kullandığı yol).
  const addAsSceneSound = async (item) => {
    if (!navItemSelected || !item.url) return;
    const dur = durations[item.id] || (await getAudioDuration(item.url));
    dispatch(
      addSound(navItemSelected, {
        file: item.url,
        name: item.name,
        startTime: 0,
        endTime: dur,
        volumeLevel: 1,
      })
    );
  };

  // Ses/klon-ses filtresinde bir dosyayı satır olarak çizer:
  // ad + play/pause + progress + süre + cue/sahne ekleme + indir.
  const renderAudioRow = (file, index) => {
    const cardId = file.id || index;
    const name = file?.name || `file-${index + 1}`;
    const url = file?.raw?.fileVirtualPath || file?.thumbnail;
    const dur = durations[cardId] || 0;
    const isActive = activeAudioIdRef.current === cardId;
    const isPlaying = playingId === cardId;
    const shown = isActive ? currentTime : 0;
    const pct = Math.min(100, (shown / (dur || 1)) * 100);
    return (
      <div
        key={cardId}
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: "10px 12px",
        }}
      >
        <div
          title={name}
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#111827",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            marginBottom: 8,
          }}
        >
          {name}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            type="button"
            onClick={() => togglePlayAudio(cardId, url)}
            title={i18n.t("listen") || i18n.t("preview")}
            style={{
              flexShrink: 0,
              width: 30,
              height: 30,
              borderRadius: "50%",
              border: "none",
              background: "#4f46e5",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            {isPlaying ? (
              <LuPause size={13} strokeWidth={2.25} />
            ) : (
              <LuPlay size={13} strokeWidth={2.25} />
            )}
          </button>

          <div
            onClick={(e) => seekAudio(cardId, url, dur, e)}
            style={{
              flex: 1,
              height: 6,
              borderRadius: 3,
              background: "#e5e7eb",
              position: "relative",
              cursor: dur ? "pointer" : "default",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                width: `${pct}%`,
                borderRadius: 3,
                background: "#4f46e5",
              }}
            />
          </div>

          <span
            style={{
              flexShrink: 0,
              fontSize: 11,
              color: "#6b7280",
              minWidth: 72,
              textAlign: "right",
            }}
          >
            {formatDuration(shown)} / {formatDuration(dur)}
          </span>

          <button
            type="button"
            onClick={() => addFileAsBox("audio", url)}
            title={i18n.t("sound_add_cue") || "Tıklama sesi olarak ekle"}
            style={miniBtnStyle}
          >
            <LuMousePointerClick size={14} strokeWidth={2.25} />
          </button>
          <button
            type="button"
            onClick={() => addAsSceneSound({ id: cardId, url, name })}
            title={i18n.t("sound_add_scene") || "Sahne sesi olarak ekle"}
            style={miniBtnStyle}
          >
            <LuClapperboard size={14} strokeWidth={2.25} />
          </button>

          <button
            type="button"
            onClick={() => downloadFile(url, name)}
            title={i18n.t("download") || "İndir"}
            style={miniBtnStyle}
          >
            <LuDownload size={14} strokeWidth={2.25} />
          </button>
        </div>
      </div>
    );
  };

  const basicFilters = [
    { key: "all", label: i18n.t("all") || "All", Icon: LuSearch },
    { key: "image", label: i18n.t("image") || "Image", Icon: LuImage },
    { key: "audio", label: i18n.t("audio") || "Audio", Icon: LuMusic2 },
    { key: "video", label: i18n.t("video") || "Video", Icon: LuClapperboard },
    { key: "pdf", label: i18n.t("pdf") || "PDF", Icon: LuFileText },
    { key: "csv", label: i18n.t("csv") || "CSV", Icon: LuFileSpreadsheet },
  ];

  const advancedFilters = [
    { key: "avatar", label: i18n.t("Avatar") || "Avatar", Icon: LuUserRound },
    { key: "clonevoice", label: i18n.t("ClonedVoices") || "Klon Ses", Icon: LuMicVocal },
    {
      key: "coursevideo",
      label: i18n.t("Storyboard") || "Storyboard Videoları",
      Icon: LuMonitorPlay,
    },
    {
      key: "animatedimage",
      label: i18n.t("AnimatedImage") || "Hareketli Resim",
      Icon: LuWandSparkles,
    },
  ];

  const addFileAsBox = async (fileType, url) => {
    if (!onBoxAdded || !url) return;

    if (fileType === "file" && typeof url === "string") {
      const cleanUrl = url.split("?")[0];
      const dotIndex = cleanUrl.lastIndexOf(".");
      if (dotIndex !== -1) {
        const extFromUrl = cleanUrl.substring(dotIndex + 1).toLowerCase();
        if (["png", "jpg", "jpeg", "webp", "gif", "bmp", "svg"].includes(extFromUrl)) {
          fileType = "image";
        } else if (["mp3", "wav", "ogg"].includes(extFromUrl)) {
          fileType = "audio";
        } else if (["mp4", "mov", "webm", "mkv"].includes(extFromUrl)) {
          fileType = "video";
        } else if (extFromUrl === "csv") {
          fileType = "csv";
        } else if (extFromUrl === "pdf") {
          fileType = "pdf";
        }
      }
    }

    const t = (fileType || "").toLowerCase();
    const resolved = resolvePlugin(t, isClm);
    const pluginName = resolved ? resolved.name : null;
    if (!pluginName) return;

    const plugin = IdealStudio.Plugins.get(pluginName);
    if (!plugin) return;

    const navItem =
      containedViewSelected && containedViews[containedViewSelected]
        ? containedViews[containedViewSelected]
        : navItems[navItemSelected];
    if (!navItem) return;

    const cv =
      containedViewSelected &&
      containedViews[containedViewSelected] &&
      isContainedView(containedViewSelected);
    const cvslide = cv && isSlide(navItem.type);
    const inASlide = (!cv && isSlide(navItem.type)) || cvslide;
    const parent = inASlide ? navItem.id : navItem.boxes && navItem.boxes[0];

    // Pozisyon + id await gerektirmez → hemen üret. Görsel/animasyon/video/ses
    // için spinner'ı TIKLAMA ANINDA göster; kutu render olunca üstüne geçip medya
    // (video/img yüklenmesi ya da <audio> metadata'sı) hazır olunca kapanır.
    const position = {
      x: randomPositionGenerator(10, 30),
      y: randomPositionGenerator(10, 30),
      type: "absolute",
    };
    const boxId = ID_PREFIX_BOX + Date.now();
    if (t === "image" || t === "animation" || t === "video" || t === "audio") {
      spinForSceneAdd({ boxId, x: position.x, y: position.y, contained: cv, assetType: t });
    }

    // Audio still clones a selected box; image/animation/video use the metadata helper.
    let width, height, widthUnit, heightUnit;
    if (t === "image" || t === "animation" || t === "video") {
      ({ width, height, widthUnit, heightUnit } = await getMediaBoxSize(
        url,
        t === "video" ? "video" : "image"
      ));
    } else if (boxSelected && pluginToolbars && pluginToolbars[boxSelected]) {
      const selStruct = pluginToolbars[boxSelected].structure;
      if (selStruct) {
        const scale = 0.6;
        const w = parseFloat(selStruct.width);
        const h = parseFloat(selStruct.height);
        width = isNaN(w) ? selStruct.width : w * scale;
        height = isNaN(h) ? selStruct.height : h * scale;
        widthUnit = selStruct.widthUnit;
        heightUnit = selStruct.heightUnit;
      }
    }

    const initialParams = {
      parent,
      container: 0,
      name: pluginName,
      // Source marker so "Replace File" reopens this same tab (Org Files).
      componentType: "OrgFiles",
      position,
      id: boxId,
      page: cv ? containedViewSelected : navItem.id || navItemSelected || 0,
      url,
      // HtmlContent embeds the file as an iframe via its _html state (a URL → iframe).
      initialState: pluginName === "HtmlContent" ? { _html: url } : undefined,
      width,
      height,
      widthUnit,
      heightUnit,
    };

    createBox(initialParams, pluginName, inASlide, onBoxAdded, boxes);
  };

  const inferFileType = (f) => {
    let ext = (f.fileExtension || "").toLowerCase().replace(/^\./, "");
    const mime = (f.fileType || f.file_type || "").toLowerCase();

    if (!ext) {
      const pathSource = (f.fileVirtualPath || f.fileName || f.filename || "").split("?")[0];
      const dotIndex = pathSource.lastIndexOf(".");
      if (dotIndex !== -1) ext = pathSource.substring(dotIndex + 1).toLowerCase();
    }

    if (mime.startsWith("image/")) return "image";
    if (mime === "animation") return "image"; // animasyonlu webp <img>'de oynar → webp olarak ekle
    if (mime === "html") return "html";
    if (mime.startsWith("audio/")) return "audio";
    if (mime.startsWith("video/")) return "video";
    if (mime === "text/csv") return "csv";
    if (mime === "application/pdf") return "pdf";
    if (["png", "jpg", "jpeg", "webp", "gif", "bmp", "svg"].includes(ext)) return "image";
    if (["mp3", "wav", "ogg", "mp3"].includes(ext)) return "audio";
    if (["mp4", "mov", "webm", "mkv"].includes(ext)) return "video";
    if (ext === "csv") return "csv";
    if (ext === "pdf") return "pdf";
    if (ext === "html" || ext === "htm") return "html";
    return "file";
  };

  // Grup listesini yükle (viewMode === "grouped" && selectedGroup === null iken)
  useEffect(() => {
    if (viewMode !== "grouped" || selectedGroup !== null) return;
    const cacheKey = `${fileTypeFilter}_${searchTerm}_${groupsPage}`;
    const cached = groupsCache.current[cacheKey];
    if (cached) {
      setGroups(cached.groups);
      setGroupsTotalPages(cached.totalPages);
      setGroupsLoading(false);
      return;
    }
    const controller = new AbortController();
    setGroupsLoading(true);
    setGroupsError(null);

    const load = async () => {
      try {
        const useExtension = fileTypeFilter === "pdf" || fileTypeFilter === "csv";
        // image/animatedimage: fileType'a ek uzantı filtresi (statik vs gif/webp).
        const extFilter = FILE_EXTENSION_MAP[fileTypeFilter];
        const response = await fetchOrgFiles({
          groupBy: true,
          page: groupsPage,
          limit: 20,
          ignoreDefaultTrainingId: true,
          search: searchTerm || undefined,
          fileType:
            fileTypeFilter === "all" || useExtension
              ? undefined
              : (FILE_TYPE_MAP[fileTypeFilter] ?? fileTypeFilter),
          fileExtension: useExtension ? [fileTypeFilter, `.${fileTypeFilter}`] : extFilter,
          signal: controller.signal,
        });
        if (response?.success && Array.isArray(response.data)) {
          const totalPages = response.pagination?.totalPages || 1;
          groupsCache.current[cacheKey] = { groups: response.data, totalPages };
          setGroups(response.data);
          setGroupsTotalPages(totalPages);
        } else {
          setGroups([]);
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          setGroupsError(i18n.t("files_load_error") || "Dosyalar yüklenemedi");
          setGroups([]);
        }
      } finally {
        if (!controller.signal.aborted) setGroupsLoading(false);
      }
    };
    const timeoutId = setTimeout(load, 250);
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [viewMode, selectedGroup, groupsPage, fileTypeFilter, searchTerm]);

  useEffect(() => {
    // grouped modda grup seçilmemişken dosya fetch etme (orada grup listesi gösterilir)
    if (viewMode === "grouped" && selectedGroup === null) return;

    const trainingKey = selectedGroup?.trainingId ?? "all";
    const cacheKey = `${trainingKey}_${fileTypeFilter}_${searchTerm}_${page}`;
    const cached = filesCache.current[cacheKey];
    if (cached) {
      setFiles(cached.files);
      setTotalPages(cached.totalPages);
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    setLoading(true);
    setError(null);

    const loadFiles = async () => {
      try {
        const useExtension = fileTypeFilter === "pdf" || fileTypeFilter === "csv";
        // image/animatedimage: fileType'a ek uzantı filtresi (statik vs gif/webp).
        const extFilter = FILE_EXTENSION_MAP[fileTypeFilter];

        const fetchParams = {
          search: searchTerm || undefined,
          limit: 20,
          page,
          groupBy: false,
          ignoreDefaultTrainingId: true, // window.idealStudioTrainingId'yi HİÇBİR ZAMAN kullanma
          fileType:
            fileTypeFilter === "all" || useExtension
              ? undefined
              : (FILE_TYPE_MAP[fileTypeFilter] ?? fileTypeFilter),
          fileExtension: useExtension ? [fileTypeFilter, `.${fileTypeFilter}`] : extFilter,
          signal: controller.signal,
        };

        // Gruba tıklandıysa sadece o eğitimin trainingId'sini ekle
        if (viewMode === "grouped" && selectedGroup !== null) {
          fetchParams.trainingId = selectedGroup.trainingId;
        }
        // list modda veya grup seçilmemişse trainingId yok → tüm org dosyaları gelir

        const response = await fetchOrgFiles(fetchParams);

        if (response?.success && Array.isArray(response.data)) {
          const mapped = response.data.map((f) => ({
            id: f.id,
            name: f.fileName,
            thumbnail: f.fileVirtualPath,
            fileType: inferFileType(f),
            raw: f,
          }));
          const totalPages = response.pagination?.totalPages || 1;
          filesCache.current[cacheKey] = { files: mapped, totalPages };
          setFiles(mapped);
          setTotalPages(totalPages);
        } else {
          setFiles([]);
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Failed to load org files", e);
          setError(i18n.t("files_load_error") || "Dosyalar yüklenemedi");
          setFiles([]);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    const timeoutId = setTimeout(loadFiles, 250);
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [searchTerm, fileTypeFilter, page, viewMode, selectedGroup]);

  // Ses satırının kaynak URL'i (renderAudioRow ile aynı) — preload/hazır kontrolü için.
  const audioRowUrl = (file) => file?.raw?.fileVirtualPath || file?.thumbnail;
  const isAudioFilter = fileTypeFilter === "audio" || fileTypeFilter === "clonevoice";
  // Tüm ses satırlarının süresi (metadata) yüklendi mi? Bitene kadar ses bölümü
  // "Yükleniyor..." gösterir; hepsi hazır olunca sesler birden çizilir.
  const audioMetaReady =
    !isAudioFilter ||
    files.every((file, index) => {
      const cardId = file.id || index;
      const url = audioRowUrl(file);
      return !url || durations[cardId] || durationFailed[cardId];
    });

  return (
    <div
      className="library-myfiles-panel"
      style={{
        flex: 1,
        maxHeight: "calc(100vh - 105px)",
        minHeight: 0,
        padding: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        className="library-myfiles-header"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          marginBottom: 16,
          padding: "0 10px",
          position: "relative",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <PanelSearch
            value={searchTerm}
            onChange={(v) => {
              filesCache.current = {};
              groupsCache.current = {};
              setSearchTerm(v);
              setPage(1);
              setGroupsPage(1);
            }}
            onClear={() => {
              filesCache.current = {};
              groupsCache.current = {};
              setSearchTerm("");
              setPage(1);
              setGroupsPage(1);
            }}
            placeholder={i18n.t("search") || "Ara"}
          />

          {/* Group Toggle Button */}
          <button
            type="button"
            onClick={() => {
              const newMode = viewMode === "list" ? "grouped" : "list";
              setViewMode(newMode);
              setSelectedGroup(null);
              setPage(1);
              setGroupsPage(1);
            }}
            title={
              viewMode === "grouped"
                ? i18n.t("show_list_view") || "Liste Görünümü"
                : i18n.t("show_grouped_view") || "Gruplu Görünüm"
            }
            style={{
              width: 40,
              height: 40,
              borderRadius: 999,
              border: "1px solid #e5e7eb",
              backgroundColor: viewMode === "grouped" ? "#f3e8ff" : "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            {viewMode === "grouped" ? (
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                stroke="#7c3aed"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              >
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                stroke="#6b7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              >
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
            )}
          </button>

          <div className="library-myfiles-filter-wrapper" style={{ position: "relative" }}>
            <button
              type="button"
              className="library-myfiles-filter-button"
              onClick={() => setIsFilterOpen((open) => !open)}
              style={{
                width: 40,
                height: 40,
                borderRadius: 999,
                border: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="#6b7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="3 4 21 4 14 12.94 14 19 10 21 10 12.94 3 4" />
              </svg>
            </button>

            {isFilterOpen && (
              <div
                className="library-myfiles-filter-dropdown"
                style={{
                  position: "absolute",
                  top: 44,
                  right: 0,
                  minWidth: 140,
                  padding: 6,
                  borderRadius: 12,
                  backgroundColor: "#ffffff",
                  boxShadow:
                    "0 10px 15px -3px rgba(15,23,42,0.15), 0 4px 6px -4px rgba(15,23,42,0.1)",
                  border: "1px solid #e5e7eb",
                  zIndex: 20,
                }}
              >
                {[...basicFilters, ...advancedFilters].map((opt) => {
                  const isActive = fileTypeFilter === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => {
                        filesCache.current = {};
                        groupsCache.current = {};
                        setFileTypeFilter(opt.key);
                        setPage(1);
                        setGroupsPage(1);
                        setIsFilterOpen(false);
                      }}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        outline: "none",
                        backgroundColor: isActive ? "#eff6ff" : "transparent",
                        color: isActive ? "#1d4ed8" : "#374151",
                        borderRadius: 8,
                        padding: "6px 8px",
                        fontSize: 13,
                        cursor: "pointer",
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {onClose && (
            <button
              type="button"
              className="cmp-panel__close"
              onClick={onClose}
              title={i18n.t("close")}
            >
              <LuX size={15} />
            </button>
          )}
        </div>
        <div
          className="library-myfiles-quickfilters"
          style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 2, alignItems: "center" }}
        >
          {basicFilters.map((opt) => {
            const active = fileTypeFilter === opt.key;
            const Icon = opt.Icon;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => {
                  filesCache.current = {};
                  groupsCache.current = {};
                  setFileTypeFilter(opt.key);
                  setPage(1);
                  setGroupsPage(1);
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "4px 8px",
                  borderRadius: 999,
                  border: "1px solid",
                  borderColor: active ? "#7c3aed" : "#e5e7eb",
                  backgroundColor: active ? "#f3e8ff" : "#ffffff",
                  color: active ? "#6b21a8" : "#374151",
                  fontSize: 12,
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                <Icon size={14} />
                <span>{opt.label}</span>
              </button>
            );
          })}

          {/* + butonu: sadece kapalıyken göster */}
          {!isAdvancedOpen && (
            <button
              type="button"
              onClick={() => setIsAdvancedOpen(true)}
              title={i18n.t("more_filters")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 26,
                height: 26,
                borderRadius: 999,
                border: "1px solid",
                borderColor: advancedFilters.some((f) => f.key === fileTypeFilter)
                  ? "#7c3aed"
                  : "#e5e7eb",
                backgroundColor: advancedFilters.some((f) => f.key === fileTypeFilter)
                  ? "#f3e8ff"
                  : "#ffffff",
                cursor: "pointer",
                flexShrink: 0,
                transition: "all 0.15s ease",
              }}
            >
              <svg
                viewBox="0 0 24 24"
                width="13"
                height="13"
                stroke={
                  advancedFilters.some((f) => f.key === fileTypeFilter) ? "#6b21a8" : "#6b7280"
                }
                strokeWidth="2.5"
                strokeLinecap="round"
                fill="none"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          )}

          {/* Gelişmiş filtreler: açıksa göster, sonda − butonu */}
          {isAdvancedOpen && (
            <>
              {advancedFilters.map((opt) => {
                const active = fileTypeFilter === opt.key;
                const Icon = opt.Icon;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                      filesCache.current = {};
                      groupsCache.current = {};
                      setFileTypeFilter(opt.key);
                      setPage(1);
                      setGroupsPage(1);
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "4px 8px",
                      borderRadius: 999,
                      border: "1px solid",
                      borderColor: active ? "#7c3aed" : "#e5e7eb",
                      backgroundColor: active ? "#f3e8ff" : "#ffffff",
                      color: active ? "#6b21a8" : "#374151",
                      fontSize: 12,
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <Icon size={14} />
                    <span>{opt.label}</span>
                  </button>
                );
              })}
              {/* − butonu: sonda, kapatmak için */}
              <button
                type="button"
                onClick={() => setIsAdvancedOpen(false)}
                title={i18n.t("collapse")}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 26,
                  height: 26,
                  borderRadius: 999,
                  border: "1px solid #e5e7eb",
                  backgroundColor: "#ffffff",
                  cursor: "pointer",
                  flexShrink: 0,
                  transition: "all 0.15s ease",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="13"
                  height="13"
                  stroke="#6b7280"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  fill="none"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Breadcrumb - Grup içindeyken göster */}
      {selectedGroup !== null && (
        <div
          style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, minWidth: 0 }}
        >
          <button
            type="button"
            onClick={() => {
              const cacheKey = `${fileTypeFilter}_${searchTerm}_${groupsPage}`;
              const cached = groupsCache.current[cacheKey];
              if (cached) {
                setGroups(cached.groups);
                setGroupsTotalPages(cached.totalPages);
                setGroupsLoading(false);
              } else {
                setGroups([]);
                setGroupsLoading(true);
              }
              setSelectedGroup(null);
              setPage(1);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 8px",
              borderRadius: 6,
              border: "none",
              backgroundColor: "transparent",
              color: "#7c3aed",
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <svg
              viewBox="0 0 24 24"
              width="13"
              height="13"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {i18n.t("org_files") || "Org. Dosyalar"}
          </button>
          <svg
            viewBox="0 0 24 24"
            width="12"
            height="12"
            stroke="#d1d5db"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0 }}
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#111827",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
            }}
          >
            {selectedGroup.trainingName}
          </span>
        </div>
      )}

      <div
        className="library-myfiles-grid"
        style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 10px 10px" }}
      >
        {/* Grup listesi görünümü - sadece grouped modda ve grup seçilmemişken */}
        {viewMode === "grouped" && selectedGroup === null && (
          <>
            {groupsLoading && (
              <div style={{ textAlign: "center", padding: 32, color: "#9ca3af", fontSize: 13 }}>
                {i18n.t("loading") || "Yükleniyor..."}
              </div>
            )}
            {groupsError && !groupsLoading && (
              <div style={{ textAlign: "center", padding: 32, color: "#ef4444", fontSize: 13 }}>
                {groupsError}
              </div>
            )}
            {!groupsLoading && !groupsError && groups.length === 0 && (
              <div style={{ textAlign: "center", padding: 32, color: "#9ca3af", fontSize: 13 }}>
                {i18n.t("no_trainings_found") || "Eğitim bulunamadı"}
              </div>
            )}
            {!groupsLoading && groups.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 8,
                }}
              >
                {groups.map((group, idx) => {
                  const name =
                    group.trainingName || i18n.t("unassigned_files") || "Atanmamış Dosyalar";
                  const count = group.fileCount || 0;
                  return (
                    <button
                      key={group.trainingId || `no-training-${idx}`}
                      type="button"
                      onClick={() => {
                        const cacheKey = `${group.trainingId}_${fileTypeFilter}_${searchTerm}_1`;
                        const cached = filesCache.current[cacheKey];
                        if (cached) {
                          setFiles(cached.files);
                          setTotalPages(cached.totalPages);
                          setLoading(false);
                        } else {
                          setFiles([]);
                          setLoading(true);
                        }
                        setSelectedGroup({ trainingId: group.trainingId, trainingName: name });
                        setPage(1);
                      }}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 8,
                        padding: "12px 10px",
                        borderRadius: 12,
                        border: "1px solid #e5e7eb",
                        backgroundColor: "#ffffff",
                        cursor: "pointer",
                        textAlign: "left",
                        transition: "all 0.15s ease",
                        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f5f3ff";
                        e.currentTarget.style.borderColor = "#a78bfa";
                        e.currentTarget.style.boxShadow = "0 4px 8px rgba(124,58,237,0.08)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#ffffff";
                        e.currentTarget.style.borderColor = "#e5e7eb";
                        e.currentTarget.style.boxShadow = "0 1px 2px rgba(15,23,42,0.04)";
                      }}
                    >
                      <span
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          backgroundColor: "#ede9fe",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="17"
                          height="17"
                          fill="none"
                          stroke="#7c3aed"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                        </svg>
                      </span>
                      <span style={{ width: "100%", minWidth: 0 }}>
                        <span
                          style={{
                            display: "block",
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#111827",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            lineHeight: "1.4",
                          }}
                        >
                          {name}
                        </span>
                        <span
                          style={{ fontSize: 11, color: "#9ca3af", marginTop: 2, display: "block" }}
                        >
                          {count} {i18n.t("files") || "dosya"}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Dosya listesi görünümü - list modda veya grouped modda grup seçildiğinde */}
        {(viewMode === "list" || (viewMode === "grouped" && selectedGroup !== null)) && (
          <>
            {loading && (
              <div style={{ textAlign: "center", padding: 32, color: "#9ca3af", fontSize: 13 }}>
                {i18n.t("loading") || "Yükleniyor..."}
              </div>
            )}
            {error && !loading && (
              <div style={{ textAlign: "center", padding: 32, color: "#ef4444", fontSize: 13 }}>
                {error}
              </div>
            )}
            {!loading && !error && files.length === 0 && (
              <EmptyState
                title={i18n.t("no_files_found") || "Dosya bulunamadı"}
                hint={i18n.t("try_other_search")}
              />
            )}
            {!loading && files.length > 0 && isAudioFilter && (
              <>
                {/* Gizli metadata preloader: her ses için süreyi (çalmadan) yükler.
                    Görünmez ama mount olduğu için tarayıcı metadata'yı çeker. */}
                <div style={{ display: "none" }}>
                  {files.map((file, index) => {
                    const cardId = file.id || index;
                    const url = audioRowUrl(file);
                    if (!url || durations[cardId] || durationFailed[cardId]) return null;
                    return (
                      <audio
                        key={`meta-${cardId}`}
                        src={url}
                        preload="metadata"
                        onLoadedMetadata={(e) => {
                          const d = e.currentTarget.duration;
                          if (isFinite(d) && d > 0)
                            setDurations((prev) =>
                              prev[cardId] ? prev : { ...prev, [cardId]: d }
                            );
                          else setDurationFailed((prev) => ({ ...prev, [cardId]: true }));
                        }}
                        onError={() => setDurationFailed((prev) => ({ ...prev, [cardId]: true }))}
                      />
                    );
                  })}
                </div>
                {audioMetaReady ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {files.map(renderAudioRow)}
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: 48,
                    }}
                  >
                    <LuLoaderCircle
                      size={32}
                      style={{ animation: "spin 1s linear infinite", color: "#4f46e5" }}
                    />
                  </div>
                )}
              </>
            )}
            {!loading &&
              files.length > 0 &&
              fileTypeFilter !== "audio" &&
              fileTypeFilter !== "clonevoice" && (
                <AssetGrid columns={3} cellSize={120} gap={10}>
                  {files.map((file, index) => {
                    const name = file?.name || file?.filename || `file-${index + 1}`;
                    const thumbnail = file?.thumbnail || file?.url || file?.src;
                    const fileType = (file?.fileType || file?.type || "file").toLowerCase();
                    const cardId = file.id || index;
                    // Animasyonlu webp video olarak eklenir: kutu/önizleme oynayabilen
                    // mp4'ü kullanır, grid küçük resmi webp kalır.
                    const url =
                      (fileType === "video" && file?.raw?.metadata?.mp4Url) ||
                      file?.raw?.fileVirtualPath ||
                      thumbnail;
                    const isReplaceMode = fileModalResult && fileModalResult.id;
                    const plugin = resolvePlugin(fileType, isClm);
                    const handleAdd = () => {
                      if (!url) return;
                      let isMatch = true;
                      if (isReplaceMode && fileModalResult?.accept && fileType) {
                        isMatch = fileModalResult.accept
                          .toLowerCase()
                          .includes(fileType.toLowerCase());
                      }
                      if (!isReplaceMode || !isMatch) return addFileAsBox(fileType, url);
                      if (onElementSelected) onElementSelected(name, url, fileType, cardId);
                    };
                    return (
                      <AssetCell
                        key={cardId}
                        ratio={0.75}
                        flush={fileType === "image" || isImageLikeUrl(thumbnail)}
                        title={name}
                        label={name}
                        dragProps={
                          plugin
                            ? {
                                className: "palette-item floatingEditorBox",
                                name: plugin.name,
                                "data-plugin": plugin.name,
                                "data-url": url,
                                "data-media": plugin.media,
                                "data-type": "OrgFiles",
                              }
                            : {}
                        }
                        onAdd={plugin ? handleAdd : undefined}
                        previewLabel={fileType === "audio" ? i18n.t("listen") : i18n.t("preview")}
                        previewIcon={
                          fileType === "audio" ? (
                            playingId === cardId ? (
                              <LuPause size={14} />
                            ) : (
                              <LuPlay size={14} />
                            )
                          ) : undefined
                        }
                        onPreview={
                          url
                            ? (e) => {
                                e.stopPropagation();
                                if (fileType === "audio") togglePlayAudio(cardId, url);
                                else setPreviewFile({ id: cardId, type: fileType, url, name });
                              }
                            : undefined
                        }
                        actions={[
                          {
                            key: "download",
                            icon: <LuDownload size={14} />,
                            title: i18n.t("download") || "İndir",
                            onClick: () => downloadFile(url, name),
                          },
                        ]}
                      >
                        {(fileType === "image" || isImageLikeUrl(thumbnail)) && thumbnail ? (
                          <LazyAssetImage
                            src={thumbnail}
                            alt={name}
                            fallback={<AssetPlaceholder type="file" />}
                          />
                        ) : (
                          <AssetPlaceholder type={fileType} />
                        )}
                      </AssetCell>
                    );
                  })}
                </AssetGrid>
              )}

            {/* Pagination - Dosya listesi için */}
            {!loading && totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 16,
                  paddingBottom: 8,
                }}
              >
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  style={{
                    padding: "4px 12px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    backgroundColor: page <= 1 ? "#f3f4f6" : "#ffffff",
                    color: page <= 1 ? "#9ca3af" : "#374151",
                    fontSize: 13,
                    cursor: page <= 1 ? "default" : "pointer",
                  }}
                >
                  {"<"}
                </button>
                <span style={{ fontSize: 13, color: "#374151" }}>
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  style={{
                    padding: "4px 12px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    backgroundColor: page >= totalPages ? "#f3f4f6" : "#ffffff",
                    color: page >= totalPages ? "#9ca3af" : "#374151",
                    fontSize: 13,
                    cursor: page >= totalPages ? "default" : "pointer",
                  }}
                >
                  {">"}
                </button>
              </div>
            )}
          </>
        )}

        {/* Pagination - Grup listesi için */}
        {viewMode === "grouped" &&
          selectedGroup === null &&
          !groupsLoading &&
          groupsTotalPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 8,
                marginTop: 16,
                paddingBottom: 8,
              }}
            >
              <button
                type="button"
                disabled={groupsPage <= 1}
                onClick={() => setGroupsPage((p) => Math.max(1, p - 1))}
                style={{
                  padding: "4px 12px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  backgroundColor: groupsPage <= 1 ? "#f3f4f6" : "#ffffff",
                  color: groupsPage <= 1 ? "#9ca3af" : "#374151",
                  fontSize: 13,
                  cursor: groupsPage <= 1 ? "default" : "pointer",
                }}
              >
                {"<"}
              </button>
              <span style={{ fontSize: 13, color: "#374151" }}>
                {groupsPage} / {groupsTotalPages}
              </span>
              <button
                type="button"
                disabled={groupsPage >= groupsTotalPages}
                onClick={() => setGroupsPage((p) => Math.min(groupsTotalPages, p + 1))}
                style={{
                  padding: "4px 12px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  backgroundColor: groupsPage >= groupsTotalPages ? "#f3f4f6" : "#ffffff",
                  color: groupsPage >= groupsTotalPages ? "#9ca3af" : "#374151",
                  fontSize: 13,
                  cursor: groupsPage >= groupsTotalPages ? "default" : "pointer",
                }}
              >
                {">"}
              </button>
            </div>
          )}
      </div>

      {previewFile && (
        <AppModal
          show
          onClose={() => setPreviewFile(null)}
          title={previewFile.name}
          centered
          size="lg"
          showSceneToggle={false}
          className="libraryPreviewModal"
        >
          {(() => {
            // URL uzantısından da tip tespit et — backend mime/extension eksik
            // veya "animation" gibi standart-dışı bir değer döndürdüğünde
            // iframe fallback'ine düşüp tarayıcının indirme tetiklemesini engeller.
            const cleanUrl = (previewFile.url || "").split("?")[0];
            const dotIndex = cleanUrl.lastIndexOf(".");
            const urlExt = dotIndex !== -1 ? cleanUrl.substring(dotIndex + 1).toLowerCase() : "";

            let resolvedType = previewFile.type;
            if (isImageLikeUrl(previewFile.url)) {
              resolvedType = "image";
            } else if (
              resolvedType !== "image" &&
              resolvedType !== "audio" &&
              resolvedType !== "video"
            ) {
              if (["png", "jpg", "jpeg", "webp", "gif", "bmp", "svg"].includes(urlExt)) {
                resolvedType = "image";
              } else if (["mp3", "wav", "ogg"].includes(urlExt)) {
                resolvedType = "audio";
              } else if (["mp4", "mov", "webm", "mkv"].includes(urlExt)) {
                resolvedType = "video";
              }
            }

            if (resolvedType === "audio") {
              return (
                <audio
                  controls
                  autoPlay
                  style={{ width: "100%", marginTop: 8 }}
                  src={previewFile.url}
                />
              );
            }
            if (resolvedType === "video") {
              return (
                <video
                  controls
                  autoPlay
                  style={{
                    width: "100%",
                    borderRadius: 12,
                    maxHeight: "70vh",
                    backgroundColor: "#000000",
                    marginTop: 4,
                  }}
                  src={previewFile.url}
                />
              );
            }
            if (resolvedType === "image") {
              return (
                <img
                  src={previewFile.url}
                  alt={previewFile.name}
                  style={{
                    width: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                    borderRadius: 12,
                    marginTop: 4,
                    backgroundColor: "#000000",
                  }}
                />
              );
            }
            return (
              <iframe
                title={previewFile.name}
                src={previewFile.url}
                style={{
                  width: "100%",
                  height: "60vh",
                  borderRadius: 12,
                  border: "1px solid #e5e7eb",
                  marginTop: 4,
                  backgroundColor: "#ffffff",
                }}
              />
            );
          })()}
        </AppModal>
      )}
    </div>
  );
};

OrgFilesPanel.propTypes = {
  isClm: PropTypes.bool,
  onNavItemsAdded: PropTypes.func.isRequired,
  onIndexSelected: PropTypes.func.isRequired,
  onNavItemSelected: PropTypes.func.isRequired,
  navItemsIds: PropTypes.array.isRequired,
  navItems: PropTypes.object.isRequired,
  navItemSelected: PropTypes.any,
  containedViews: PropTypes.object.isRequired,
  containedViewSelected: PropTypes.any,
  boxes: PropTypes.object.isRequired,
  onBoxAdded: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

export default OrgFilesPanel;
